/**
 * Call-logs (NOT-15).
 *
 * Routed through the operations proxy at `/api/voice/call-logs`. Operations
 * forwards the caller's notifi ES256 Bearer + `x-tenant-id` to the calling
 * service (system of record, Decision D) and translates our `inbound`/`outbound`
 * direction to the CS's `incoming`/`outgoing`. The browser never talks to the CS
 * directly, so it needs neither the CS host nor a CORS grant.
 */

import { apiDelete, apiGet, apiPatch, apiPost, apiPut, operationsClient } from '@/lib/api';
import type {
  CallLogDetailResponse,
  CallLogsResponse,
  CallReportParams,
  CallReportResponse,
  UnbookedCallsParams,
  UnbookedCallsResponse,
  CallFollowUpResponse,
  CallFollowUpsResponse,
  CallFollowUpDisposition,
  CallFollowUpActivityResponse,
  CallOutcomePolarity,
  CallOutcomeResponse,
  CallOutcomeSuggestionResponse,
  CallOutcomesResponse,
  FollowUpAssigneesResponse,
  FollowUpCommitmentInput,
  ActionNeededReportParams,
  ActionNeededReportResponse,
  ListCallFollowUpsParams,
  CallSummaryResponse,
  CallTranscribeResponse,
  ListCallLogsParams,
} from './types';
import type { CallReportsParams, CallReportsResponse } from './callReportTypes';

const BASE = '/api/voice/call-logs';
/** The outcome vocabulary is operations' own config, not a call-log read, so
 *  it sits beside the proxy rather than under it. */
const OUTCOMES = '/api/voice/call-outcomes';
const callLogsUrl = {
  list: () => BASE,
  detail: () => `${BASE}/detail`,
  report: () => `${BASE}/report`,
  reports: () => `${BASE}/reports`,
  unbooked: () => `${BASE}/unbooked`,
  actionNeeded: () => `${BASE}/action-needed`,
  actionNeededReport: () => `${BASE}/action-needed/report`,
  followUpAssignees: () => `${BASE}/action-needed/assignees`,
  followUpAction: (callControlId: string, action: string) =>
    `${BASE}/action-needed/${encodeURIComponent(callControlId)}/${action}`,
  summary: () => `${BASE}/summary`,
  transcribe: () => `${BASE}/transcribe`,
};

/** Drop undefined/null/empty values so we don't send blank query params. */
function cleanParams(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      out[key] = value;
    }
  }
  return out;
}

export const callsApi = {
  // Send inbound/outbound as-is — the operations proxy maps it to the CS's
  // incoming/outgoing (its DTO validates inbound/outbound).
  list: (params: ListCallLogsParams = {}) =>
    apiGet<CallLogsResponse>(operationsClient, callLogsUrl.list(), {
      params: cleanParams(params as Record<string, unknown>),
    }),

  detail: (callControlId: string) =>
    apiGet<CallLogDetailResponse>(operationsClient, callLogsUrl.detail(), {
      params: { callControlId },
    }),

  report: (params: CallReportParams = {}) =>
    apiGet<CallReportResponse>(operationsClient, callLogsUrl.report(), {
      params: cleanParams(params as Record<string, unknown>),
    }),

  /** Inbound calls in the window that were abandoned or never booked. Takes
   *  the report's own period so the list describes the same window as the
   *  numbers above it. */
  unbooked: (params: UnbookedCallsParams) =>
    apiGet<UnbookedCallsResponse>(operationsClient, callLogsUrl.unbooked(), {
      params: cleanParams(params as Record<string, unknown>),
    }),

  summary: (callControlId: string, body?: Record<string, unknown>) =>
    apiPost<CallSummaryResponse>(operationsClient, callLogsUrl.summary(), body ?? {}, {
      params: { callControlId },
    }),

  transcribe: (callControlId: string) =>
    apiPost<CallTranscribeResponse>(
      operationsClient,
      callLogsUrl.transcribe(),
      {},
      { params: { callControlId } },
    ),

  /**
   * The Action Needed queue. Not a proxy like its neighbours — these rows are
   * operations' own, so there is no calling-service contract to mirror here.
   */
  /** One request for the whole Reports tab. Same filters as the list below. */
  /** The whole Reports page, computed once on the server. */
  reports: (params: CallReportsParams) =>
    apiGet<CallReportsResponse>(operationsClient, callLogsUrl.reports(), {
      params: cleanParams(params as unknown as Record<string, unknown>),
    }),

  actionNeededReport: (params: ActionNeededReportParams = {}) =>
    apiGet<ActionNeededReportResponse>(operationsClient, callLogsUrl.actionNeededReport(), {
      params,
    }),

  actionNeeded: (params: ListCallFollowUpsParams = {}) =>
    apiGet<CallFollowUpsResponse>(operationsClient, callLogsUrl.actionNeeded(), {
      params: cleanParams(params as Record<string, unknown>),
    }),

  /**
   * Close a row. `outcomeId` is what the outcome sheet sends — it carries the
   * disposition the server stamps. `disposition` remains for the quick "mark
   * resolved" button; the server requires exactly one of the two.
   */
  resolveFollowUp: (
    callControlId: string,
    body: {
      outcomeId?: string;
      disposition?: CallFollowUpDisposition;
      note?: string;
      /** A next step that survives the close. Omitted, the close is the end
       *  of the matter and the task behind the row is cancelled. */
      followUp?: FollowUpCommitmentInput;
      /** Change the outcome of a call that is already closed, leaving it
       *  closed. Needs `outcomeId`. Without it a closed call is refused. */
      amend?: boolean;
    },
  ) =>
    apiPost<CallFollowUpResponse>(
      operationsClient,
      callLogsUrl.followUpAction(callControlId, 'resolve'),
      body,
    ),

  /** One call's follow-up, or `data: null` when it never needed one — most
   *  calls are answered and booked and never open a row at all. */
  followUp: (callControlId: string) =>
    apiGet<CallFollowUpResponse>(
      operationsClient,
      `${callLogsUrl.actionNeeded()}/${encodeURIComponent(callControlId)}`,
    ),

  /** People the signed-in user may hand a follow-up to, scope-filtered. */
  followUpAssignees: () =>
    apiGet<FollowUpAssigneesResponse>(operationsClient, callLogsUrl.followUpAssignees()),

  assignFollowUp: (callControlId: string, userId: string | null) =>
    apiPost<CallFollowUpResponse>(
      operationsClient,
      callLogsUrl.followUpAction(callControlId, 'assign'),
      { userId },
    ),

  /** Records that somebody tried to reach the caller. Deliberately not a
   *  resolution — the attempt history is what tells the next agent what has
   *  already been tried. */
  recordFollowUpAttempt: (callControlId: string, channel: 'call' | 'sms') =>
    apiPost<CallFollowUpResponse>(
      operationsClient,
      callLogsUrl.followUpAction(callControlId, 'attempt'),
      { channel },
    ),

  /** Everything that has happened to this call, newest first — notes AND the
   *  system's own entries, out of one table and in one order. */
  followUpActivity: (callControlId: string) =>
    apiGet<CallFollowUpActivityResponse>(
      operationsClient,
      callLogsUrl.followUpAction(callControlId, 'activity'),
    ),

  addFollowUpNote: (callControlId: string, body: string) =>
    apiPost<CallFollowUpResponse>(
      operationsClient,
      callLogsUrl.followUpAction(callControlId, 'notes'),
      { body },
    ),

  /** Commit to a next step, or clear it (`at: null`). */
  scheduleFollowUp: (callControlId: string, body: FollowUpCommitmentInput) =>
    apiPost<CallFollowUpResponse>(
      operationsClient,
      callLogsUrl.followUpAction(callControlId, 'schedule'),
      body,
    ),

  /** Undo a resolution. The counterpart to a one-click close, which is the
   *  easiest thing on this screen to do by accident. */
  reopenFollowUp: (callControlId: string) =>
    apiPost<CallFollowUpResponse>(
      operationsClient,
      callLogsUrl.followUpAction(callControlId, 'reopen'),
      {},
    ),

  /** Say which customer an unmatched caller actually is. Caller-ID matching
   *  runs once, when the call ends; a number nobody had on file then stays a
   *  stranger forever without this. */
  linkFollowUpContact: (callControlId: string, contactId: string) =>
    apiPost<CallFollowUpResponse>(
      operationsClient,
      callLogsUrl.followUpAction(callControlId, 'contact'),
      { contactId },
    ),

  /**
   * Iris's suggested outcome. The ONLY Iris-backed call-log read there is —
   * the server refuses it for a voicemail row, and answers `data: null`
   * whenever it has nothing honest to suggest.
   */
  outcomeSuggestion: (callControlId: string) =>
    apiGet<CallOutcomeSuggestionResponse>(
      operationsClient,
      callLogsUrl.followUpAction(callControlId, 'suggestion'),
    ),

  update: (callControlId: string, body: Record<string, unknown>) =>
    apiPut<CallLogDetailResponse>(operationsClient, callLogsUrl.list(), body, {
      params: { callControlId },
    }),
};

/**
 * The tenant's call-outcome vocabulary.
 *
 * Reading needs `messages:view` (every CSR picks from this list); writing
 * needs `company:edit`, the same gate Call Settings edits behind — the
 * vocabulary is tenant-wide config every report keys on.
 *
 * There is no delete: outcomes are archived, because closed follow-ups
 * reference them.
 */
export const callOutcomesApi = {
  list: () => apiGet<CallOutcomesResponse>(operationsClient, OUTCOMES),

  create: (body: { label: string; polarity: CallOutcomePolarity; sortOrder?: number }) =>
    apiPost<CallOutcomeResponse>(operationsClient, OUTCOMES, body),

  update: (
    outcomeId: string,
    body: {
      label?: string;
      polarity?: CallOutcomePolarity;
      sortOrder?: number;
      isActive?: boolean;
    },
  ) =>
    apiPatch<CallOutcomeResponse>(
      operationsClient,
      `${OUTCOMES}/${encodeURIComponent(outcomeId)}`,
      body,
    ),

  /**
   * Deletes an outcome nothing has been filed under. The server answers 409
   * for one that calls reference — archive that instead, so those calls keep
   * reading correctly.
   */
  remove: (outcomeId: string) =>
    apiDelete<{ success: boolean }>(
      operationsClient,
      `${OUTCOMES}/${encodeURIComponent(outcomeId)}`,
    ),
};
